<template>
  <div v-if="loading" class="form-detail-loading">
    <div class="form-detail-spinner"></div>
    <p>Loading form details...</p>
  </div>
  <div v-else-if="accessDenied" class="form-detail-loading">
    <p style="color: #d32f2f; font-weight: bold;">You are not allowed to access this form.</p>
    <router-link to="/forms" class="btn btn-link" style="margin-top: 1.5em;">Back to Forms</router-link>
  </div>
  <div v-else-if="forms && forms.length" class="form-detail-root">
    <div class="form-detail-card">
      <h2 class="form-detail-title">{{ currentForm.title }}</h2>

      <div class="form-detail-meta">
        <div v-if="currentForm.subjectNames && currentForm.subjectNames.length" class="form-detail-meta-row">
          <span class="form-detail-meta-label">Subjects:</span>
          <div class="form-detail-meta-chips">
            <span
              v-for="subject in currentForm.subjectNames"
              :key="subject"
              class="meta-chip"
            >
              {{ subject }}
            </span>
          </div>
        </div>
        <div v-if="currentForm.teacherNames && currentForm.teacherNames.length" class="form-detail-meta-row">
          <span class="form-detail-meta-label">Teachers:</span>
          <div class="form-detail-meta-chips">
            <span
              v-for="teacher in currentForm.teacherNames"
              :key="teacher"
              class="meta-chip"
            >
              {{ teacher }}
            </span>
          </div>
        </div>
      </div>

      <form @submit.prevent="handleFormSubmit" class="form-detail-form">
        <div
          v-for="(question, idx) in currentForm.questions"
          :key="question.id"
          class="form-detail-question"
        >
          <div class="form-detail-question-header">
            <span class="form-detail-question-number">{{ idx + 1 }}.</span>
            <span class="form-detail-question-text">{{ question.text }}</span>
          </div>
          <div
            v-if="question.type === 'range_feedback'"
            class="form-detail-options"
          >
            <label
              v-for="option in question.options"
              :key="option.id"
              class="form-detail-option"
              :class="{ selected: answers[currentForm.id] && answers[currentForm.id][question.id] == option.id }"
              tabindex="0"
            >
              <input
                type="radio"
                :name="'question_' + question.id"
                :value="option.id"
                v-model="answers[currentForm.id][question.id]"
                required
                class="form-detail-radio"
              />
              <span>{{ option.text }}</span>
            </label>
          </div>
          <div v-else-if="question.type === 'qna'" class="form-detail-qna">
            <input
              type="text"
              class="form-detail-qna-input"
              v-model="answers[currentForm.id][question.id]"
              :placeholder="'Type your answer...'"
              required
            />
          </div>
        </div>
        <div class="form-detail-actions">
          <button 
            v-if="currentStep > 0" 
            type="button" 
            class="btn btn-secondary" 
            @click="goToPreviousStep"
          >
            Previous
          </button>
          <button 
            v-if="currentStep < forms.length - 1" 
            type="submit" 
            class="btn btn-primary"
            :disabled="!isCurrentStepValid"
          >
            Next
          </button>
          <button 
            v-if="currentStep === forms.length - 1" 
            type="button" 
            class="btn btn-primary"
            :disabled="!isCurrentStepValid"
            @click="openConfirmModal"
          >
            Submit All Forms
          </button>
          <router-link to="/forms" class="btn btn-link">Back to Forms</router-link>
        </div>
      </form>
    </div>

    <div class="step-indicator">
      Step {{ currentStep + 1 }} of {{ forms.length }}
    </div>
  </div>

  <div v-if="showConfirmModal" class="modal-overlay">
    <div class="modal-content">
      <h3>Confirm Submission</h3>
      <p>Are you sure you want to submit all {{ forms.length }} forms?</p>
      <div class="modal-actions">
        <button
          class="btn btn-primary"
          :disabled="submitting"
          @click="submitAllAnswers"
        >
          <span v-if="submitting">
            <span class="form-detail-spinner" style="width:20px;height:20px;border-width:3px;margin-bottom:0;margin-right:8px;"></span>
            Submitting...
          </span>
          <span v-else>Yes, Submit All</span>
        </button>
        <button class="btn btn-link" @click="showConfirmModal = false" :disabled="submitting">Cancel</button>
      </div>
    </div>
  </div>

  <div v-if="showResultModal" class="modal-overlay">
    <div class="modal-content">
      <template v-if="submitting">
        <div class="form-detail-spinner" style="margin: 0 auto 1em auto;"></div>
        <p>Submitting your answers...</p>
      </template>
      <template v-else>
        <h3 v-if="resultSuccess" style="color: #2563eb;">Success</h3>
        <h3 v-else style="color: #d32f2f;">Error</h3>
        <p>{{ resultMessage }}</p>
        <div class="modal-actions">
          <button class="btn btn-primary" @click="handleResultModalClose">OK</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { fetchFormDetail, submitFormAnswers, checkUserFormAccess } from "../../services/formService";
import { mapState } from "vuex";

export default {
  props: ["formId"], 
  data() {
    return {
      forms: [],
      currentStep: 0,
      answers: {},
      loading: true,
      accessDenied: false,
      showConfirmModal: false,
      showResultModal: false,
      resultSuccess: false,
      resultMessage: "",
      submitting: false, 
    };
  },
  computed: {
    ...mapState(["profileInfo"]),
    currentForm() {
      return this.forms[this.currentStep] || {};
    },
    isCurrentStepValid() {
      if (!this.currentForm || !this.currentForm.questions) return false;
      
      const formAnswers = this.answers[this.currentForm.id] || {};
      return this.currentForm.questions.every(question => {
        const answer = formAnswers[question.id];
        return answer !== undefined && answer !== null && answer !== "";
      });
    }
  },
  async created() {
    const parentFormId = this.formId || this.$route.params.formId; 
    if (!this.profileInfo?.userId) {
      await this.$store.dispatch("fetchProfileInfo");
    }
    try {
      const res = await checkUserFormAccess(this.profileInfo.userId, parentFormId); 
      if (!(res && res.message === "User can access this form")) {
        this.accessDenied = true;
        this.loading = false;
        return;
      }
    } catch (e) {
      this.accessDenied = true;
      this.loading = false;
      return;
    }
    
    const formData = await fetchFormDetail(parentFormId); 
    this.forms = Array.isArray(formData) ? formData : [formData];
    
    this.forms.forEach(form => {
      this.answers[form.id] = {};
      if (form.questions) {
        form.questions.forEach(q => {
          this.answers[form.id][q.id] = "";
        });
      }
    });
    
    this.loading = false;
  },
  methods: {
    handleFormSubmit() {
      if (this.isCurrentStepValid && this.currentStep < this.forms.length - 1) {
        this.goToNextStep();
      }
    },
    goToNextStep() {
      if (this.currentStep < this.forms.length - 1) {
        this.currentStep++;
      }
    },
    goToPreviousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    openConfirmModal() {
      this.showConfirmModal = true;
    },
    async submitAllAnswers() {
      const parentFormId = this.$route.params.formId; 
      this.submitting = true;
      this.showConfirmModal = false;
      this.showResultModal = true;

      if (!this.profileInfo?.userId) {
        await this.$store.dispatch("fetchProfileInfo");
      }

      const roll_number = this.profileInfo?.organization?.rollNumber || "";

      try {
        const access = await checkUserFormAccess(this.profileInfo.userId, parentFormId);
        if (!(access && access.message === "User can access this form")) {
          throw { error: "You are not allowed to submit this form." };
        }

        const allResponses = this.forms.map((form) => {
          return form.questions.map((q) => {
            const answer = this.answers[form.id][q.id];
            if (q.type === "range_feedback") {
              return {
                questionId: Number(q.id),
                roll_number,
                optionId: Number(answer),
                formId: form.id,
              };
            } else if (q.type === "qna") {
              return {
                questionId: Number(q.id),
                roll_number,
                answer: answer,
                formId: form.id, 
              };
            }
            return null;
          }).filter(Boolean);
        }).flat();

        const payload = {
          responses: allResponses,
          isStudent: true,
        };

        console.log("Submitting payload:", payload);
        await submitFormAnswers(parentFormId, payload); 

        this.forms.forEach((form) => {
          form.questions.forEach((q) => {
            this.answers[form.id][q.id] = "";
          });
        });

        this.resultSuccess = true;
        this.resultMessage = `All ${this.forms.length} forms submitted successfully!`;
      } catch (e) {
        this.resultSuccess = false;
        this.resultMessage = e?.error || "Submission failed!";
      }

      this.submitting = false;
    },
    handleResultModalClose() {
      this.showResultModal = false;
      if (this.resultSuccess) {
        this.$router.push({ name: "formsList" });
      }
    }
  }
};
</script>

<style scoped>

.progress-container {
  max-width: 900px;
  margin: 0 auto 2rem auto;
  padding: 0 1.5rem;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e0e0e0; 
  border-radius: 4px; 
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #007bff; 
  transition: width 0.3s ease; 
}

.progress-text {
  text-align: center;
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.025em;
}

.form-detail-root {
  width: 100%;
  padding: 1rem 0;
}

.form-detail-card {
  width: 100%;
  margin: 0 auto;
  background: #f9f9f9; 
  border-radius: 4px; 
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); 
  padding: 1.5rem;
  border: 1px solid #e0e0e0; 
}

.form-detail-title {
  font-size: 1.25rem; 
  font-weight: 500; 
  margin-bottom: 1rem;
  text-align: center;
  color: #333; 
}

.form-detail-meta {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #ffffff; 
  border-radius: 4px; 
  border: 1px solid #e0e0e0; 
}

.form-detail-meta-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.form-detail-meta-row:last-child {
  margin-bottom: 0;
}

.form-detail-meta-label {
  font-weight: 500; 
  color: #555; 
  font-size: 0.875rem; 
  margin-top: 0.25rem;
}

.form-detail-meta-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem; 
}

.meta-chip {
  padding: 0.25rem 0.75rem;
  border-radius: 12px; 
  font-size: 0.75rem; 
  font-weight: 500;
  background: #e0e0e0; 
  color: #333; 
}

.form-detail-form {
  margin-top: 2rem;
}

.form-detail-question {
  margin-bottom: 1.5rem; /* Reduced spacing */
  padding: 1rem;
  background: #ffffff; /* Neutral background */
  border-radius: 4px; /* Reduced border radius */
  border: 1px solid #e0e0e0; /* Neutral border color */
}

.form-detail-question-header {
  font-weight: 500; /* Lighter font weight */
  margin-bottom: 1rem;
  font-size: 1rem; /* Reduced font size */
  color: #333; /* Neutral text color */
  display: flex;
  align-items: flex-start;
  line-height: 1.5;
}

.form-detail-question-number {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;
  font-weight: 700;
  min-width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 0.875rem;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.form-detail-question-text {
  flex: 1;
  margin-top: 0.125rem;
}

.form-detail-options {
  display: flex;
  flex-direction: column; /* Simplified layout */
  gap: 0.5rem; /* Reduced gap */
}

.form-detail-option {
  display: flex;
  align-items: center;
  background: #f9f9f9; /* Subtle background */
  border-radius: 4px; /* Reduced border radius */
  padding: 0.5rem 1rem; /* Reduced padding */
  border: 1px solid #e0e0e0; /* Neutral border color */
  font-size: 0.875rem; /* Reduced font size */
  cursor: pointer;
}

.form-detail-option:hover {
  background: #e0e0e0; /* Subtle hover effect */
}

.form-detail-radio {
  margin-right: 0.5rem; /* Reduced spacing */
}

.form-detail-qna {
  margin-top: 1rem;
}

.form-detail-qna-input {
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  background: #ffffff;
  transition: all 0.2s ease;
  font-family: inherit;
}

.form-detail-qna-input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background: #fafbfc;
}

.form-detail-qna-input::placeholder {
  color: #94a3b8;
}

.form-detail-actions {
  margin-top: 2rem; /* Reduced spacing */
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.btn {
  padding: 0.5rem 1rem; /* Reduced padding */
  border-radius: 4px; /* Reduced border radius */
  font-size: 0.875rem; /* Reduced font size */
  font-weight: 500; /* Lighter font weight */
}

.btn-primary {
  background: #007bff; /* Subtle blue */
  color: #ffffff;
}

.btn-primary:hover {
  background: #0056b3; /* Darker blue */
}

.btn-secondary {
  background: #6c757d; /* Neutral gray */
  color: #ffffff;
}

.btn-secondary:hover {
  background: #5a6268; /* Darker gray */
}

.btn-link {
  background: transparent;
  color: #007bff; /* Subtle blue */
  text-decoration: none;
}

.btn-link:hover {
  color: #0056b3; /* Darker blue */
}

.form-detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.form-detail-loading p {
  color: #64748b;
  font-size: 1.125rem;
  font-weight: 500;
}

.form-detail-spinner {
  border: 3px solid rgba(59, 130, 246, 0.1);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: #ffffff;
  padding: 2.5rem;
  border-radius: 16px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  text-align: center;
  border: 1px solid rgba(226, 232, 240, 0.8);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-content h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1e293b;
}

.modal-content p {
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.step-indicator {
  text-align: center;
  font-size: 0.875rem; /* Slightly smaller font size */
  color: #555; /* Neutral text color */
  font-weight: 500; /* Medium font weight */
  margin-top: 2rem; /* Add spacing from the form */
  padding: 0.5rem 0; /* Add padding for better visibility */
  border-top: 1px solid #e0e0e0; /* Subtle border to separate from content */
}

@media (max-width: 1100px) {
  .form-detail-card {
    max-width: 95vw;
    padding: 2rem 1.5rem;
    margin: 0 auto;
  }
  
  .progress-container {
    padding: 0 1rem;
  }
  
  .form-detail-options {
    grid-template-columns: 1fr;
  }
  
  .form-detail-actions {
    justify-content: center;
    flex-direction: column-reverse;
  }
  
  .btn {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 600px) {
  .form-detail-card {
    padding: 1.5rem 1rem;
    max-width: 98vw;
    border-radius: 12px;
  }
  
  .modal-content {
    padding: 1.5rem;
    max-width: 95vw;
    border-radius: 12px;
  }
  
  .form-detail-title {
    font-size: 1.5rem;
  }
  
  .progress-container {
    padding: 0 1rem;
  }
  
  .form-detail-question {
    padding: 1.5rem;
  }
  
  .form-detail-question-header {
    font-size: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .form-detail-question-number {
    margin-right: 0;
    align-self: flex-start;
  }
  
  .form-detail-meta {
    padding: 1rem;
  }
  
  .form-detail-meta-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .form-detail-meta-label {
    min-width: auto;
    margin-top: 0;
  }
  
  .modal-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .modal-actions .btn {
    width: 100%;
  }
}
</style>